import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Mail, Building2, ShieldCheck, FileText, Users, Lock, Database, Bot, Clock, UserCheck, MessageSquare, RefreshCw, Phone } from "lucide-react";
import "@/App.css";

const sections = [
  { id: "introduction", label: "1. Introduction" },
  { id: "legal-entity", label: "2. Legal Entity" },
  { id: "about", label: "3. About Precision Prescribing" },
  { id: "information", label: "4. Information We Process" },
  { id: "how-we-use", label: "5. How We Use Information" },
  { id: "legal-basis", label: "6. Legal Basis for Processing" },
  { id: "client-platforms", label: "7. Client-Controlled Platforms" },
  { id: "data-sharing", label: "8. Data Sharing" },
  { id: "security", label: "9. Information Security" },
  { id: "external-tools", label: "10. External Tools & AI" },
  { id: "retention", label: "11. Data Retention" },
  { id: "rights", label: "12. Individual Rights" },
  { id: "patient-requests", label: "13. Patient-Facing Requests" },
  { id: "changes", label: "14. Changes to This Policy" },
  { id: "contact", label: "15. Contact Us" },
];

// Accordion section (moved outside PrivacyPolicy to avoid re-mounting on parent renders)
const AccordionSection = ({ id, title, icon: Icon, isOpen, onToggle, children, testId }) => (
  <section id={id} className="privacy-block privacy-accordion" data-testid={testId}>
    <button
      className="privacy-accordion-header"
      onClick={onToggle}
      data-testid={`${testId}-toggle`}
      aria-expanded={isOpen}
    >
      <span className="privacy-accordion-title">
        <Icon size={20} className="privacy-accordion-icon" />
        <span>{title}</span>
      </span>
      <ChevronDown size={22} className={`privacy-chevron ${isOpen ? "privacy-chevron-open" : ""}`} />
    </button>
    {isOpen && (
      <div className="privacy-accordion-body" data-testid={`${testId}-body`}>
        {children}
      </div>
    )}
  </section>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openAccordions, setOpenAccordions] = useState({});
  const [activeSection, setActiveSection] = useState("introduction");
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Track active section for TOC highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    setMobileMenuOpen(false);
    navigate("/", { state: { scrollTo: "contact" } });
  };

  const handleMobileNavClick = (action) => {
    setMobileMenuOpen(false);
    if (typeof action === "function") action();
  };

  // Toggle accordion
  const toggleAccordion = (id) => {
    setOpenAccordions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const scrollToSection = (id) => {
    setTocOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -96;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Reusable accordion wrapper for long sections


  return (
    <div className="privacy-page" data-testid="privacy-page">
      {/* Navigation */}
      <nav className="nav" data-testid="navigation">
        <div className="nav-container">
          <div className="nav-logo" data-testid="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <span className="logo-text">Precision Prescribing</span>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div className="nav-links">
            <button onClick={() => navigate("/clinical-areas")} className="nav-link" data-testid="nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => navigate("/dispensing")} className="nav-link" data-testid="nav-dispensing">Dispensing</button>
            <button onClick={() => navigate("/#how-it-works")} className="nav-link" data-testid="nav-how-it-works">How It Works</button>
            <button onClick={() => navigate("/#about")} className="nav-link" data-testid="nav-about">About</button>
            <button onClick={scrollToContact} className="nav-link nav-cta" data-testid="nav-contact-cta">Request a Discussion</button>
          </div>

          <div className={`mobile-nav ${mobileMenuOpen ? "mobile-nav-open" : ""}`} data-testid="mobile-nav">
            <button onClick={() => handleMobileNavClick(() => navigate("/clinical-areas"))} className="mobile-nav-link" data-testid="mobile-nav-clinical-areas">Clinical Areas</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/dispensing"))} className="mobile-nav-link" data-testid="mobile-nav-dispensing">Dispensing</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/#how-it-works"))} className="mobile-nav-link" data-testid="mobile-nav-how-it-works">How It Works</button>
            <button onClick={() => handleMobileNavClick(() => navigate("/#about"))} className="mobile-nav-link" data-testid="mobile-nav-about">About</button>
            <button onClick={() => handleMobileNavClick(scrollToContact)} className="mobile-nav-link mobile-nav-cta" data-testid="mobile-nav-contact-cta">Request a Discussion</button>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <section className="privacy-hero" data-testid="privacy-hero">
        <div className="privacy-hero-inner">
          <span className="privacy-eyebrow" data-testid="privacy-eyebrow">Legal</span>
          <h1 className="privacy-title" data-testid="privacy-title">Privacy Policy</h1>
          <p className="privacy-subtitle" data-testid="privacy-subtitle">
            How PHARMKG LTD, trading as Precision Prescribing, collects, uses, stores and protects personal information.
          </p>
          <div className="privacy-meta" data-testid="privacy-meta">
            <span className="privacy-meta-item"><Clock size={16} /> Last updated: 12 August 2026</span>
            <span className="privacy-meta-item"><ShieldCheck size={16} /> UK GDPR Aligned</span>
          </div>
        </div>
      </section>

      {/* Mobile TOC toggle */}
      <div className="privacy-toc-mobile" data-testid="privacy-toc-mobile">
        <button
          className="privacy-toc-mobile-btn"
          onClick={() => setTocOpen(!tocOpen)}
          data-testid="privacy-toc-mobile-btn"
          aria-expanded={tocOpen}
        >
          <span>On this page</span>
          <ChevronDown size={18} className={`privacy-chevron ${tocOpen ? "privacy-chevron-open" : ""}`} />
        </button>
        {tocOpen && (
          <ul className="privacy-toc-mobile-list" data-testid="privacy-toc-mobile-list">
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  className={`privacy-toc-link ${activeSection === s.id ? "privacy-toc-link-active" : ""}`}
                  onClick={() => scrollToSection(s.id)}
                  data-testid={`privacy-toc-mobile-link-${s.id}`}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Main Layout: Sticky TOC + Content */}
      <div className="privacy-layout" data-testid="privacy-layout">
        {/* Sticky Sidebar TOC (desktop) */}
        <aside className="privacy-toc" data-testid="privacy-toc">
          <div className="privacy-toc-sticky">
            <p className="privacy-toc-label">On this page</p>
            <ul className="privacy-toc-list">
              {sections.map((s) => (
                <li key={s.id}>
                  <button
                    className={`privacy-toc-link ${activeSection === s.id ? "privacy-toc-link-active" : ""}`}
                    onClick={() => scrollToSection(s.id)}
                    data-testid={`privacy-toc-link-${s.id}`}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Content */}
        <article className="privacy-article" data-testid="privacy-article">
          {/* 1. Introduction */}
          <section id="introduction" className="privacy-block" data-testid="privacy-section-introduction">
            <h2><FileText size={22} className="privacy-h2-icon" /> 1. Introduction</h2>
            <p>Precision Prescribing is a trading name/service operated by <strong>PHARMKG LTD</strong>.</p>
            <p>This Privacy Policy explains how PHARMKG LTD, trading as Precision Prescribing, collects, uses, stores and protects personal information in connection with the Precision Prescribing service.</p>
            <p>Precision Prescribing provides pharmacist, prescriber and clinical support services to client organisations. This may include support with prescribing tasks, clinical reviews, audit, governance, onboarding, training and related professional services.</p>
            <p>This policy applies to personal data processed in connection with our services. It is intended for client organisations, client contacts, pharmacists, prescribers, contractors and other individuals whose information may be processed as part of the service.</p>
          </section>

          {/* 2. Legal Entity */}
          <section id="legal-entity" className="privacy-block" data-testid="privacy-section-legal-entity">
            <h2><Building2 size={22} className="privacy-h2-icon" /> 2. Legal Entity</h2>
            <p>Precision Prescribing is not the legal entity. The legal entity responsible for the Precision Prescribing service is:</p>
            <div className="privacy-callout" data-testid="privacy-entity-card">
              <Building2 size={24} className="privacy-callout-icon" />
              <div>
                <p className="privacy-callout-title">PHARMKG LTD</p>
                <p className="privacy-callout-sub">Trading as Precision Prescribing</p>
              </div>
            </div>
            <p>Where we refer to &ldquo;Precision Prescribing&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo; in this Privacy Policy, we mean PHARMKG LTD trading as Precision Prescribing.</p>
          </section>

          {/* 3. About */}
          <section id="about" className="privacy-block" data-testid="privacy-section-about">
            <h2><ShieldCheck size={22} className="privacy-h2-icon" /> 3. About Precision Prescribing</h2>
            <p>Precision Prescribing provides pharmacist, prescriber and clinical support services to client organisations.</p>
            <p>Our personnel may work within a client&rsquo;s approved:</p>
            <ul className="privacy-list-grid">
              <li>Clinical platform</li>
              <li>Prescribing system</li>
              <li>Workflows</li>
              <li>Standard operating procedures</li>
              <li>Clinical pathways</li>
              <li>Escalation routes</li>
              <li>Governance framework</li>
              <li>Communication channels</li>
            </ul>
            <div className="privacy-info-card" data-testid="privacy-about-note">
              <p><strong>Important:</strong> Where services are delivered through a client-controlled platform, Precision Prescribing does not usually operate the patient-facing platform, does not independently host patient records, and does not maintain a separate clinical record system for those patients unless expressly agreed in writing.</p>
              <p style={{ marginBottom: 0 }}>Patient records and core clinical data usually remain within the relevant client-controlled system.</p>
            </div>
          </section>

          {/* 4. Information We Process (accordion) */}
          <AccordionSection
            id="information"
            title="4. Information We May Process"
            icon={Database}
            isOpen={!!openAccordions.information}
            onToggle={() => toggleAccordion("information")}
            testId="privacy-section-information"
          >
            <p>Depending on the service provided, we may process the following categories of information.</p>

            <div className="privacy-subcard" data-testid="privacy-info-client">
              <h4>Client and business contact information</h4>
              <ul>
                <li>Name</li>
                <li>Job title</li>
                <li>Organisation</li>
                <li>Work email address</li>
                <li>Work telephone number</li>
                <li>Correspondence</li>
                <li>Contract and onboarding information</li>
                <li>Supplier assurance information</li>
              </ul>
            </div>

            <div className="privacy-subcard" data-testid="privacy-info-pharmacist">
              <h4>Pharmacist, prescriber and contractor information</h4>
              <ul>
                <li>Name and contact details</li>
                <li>Professional registration details</li>
                <li>GPhC number</li>
                <li>Prescribing qualification details, where relevant</li>
                <li>Professional indemnity or insurance information</li>
                <li>DBS status, where required</li>
                <li>Right-to-work or identity verification information</li>
                <li>CV or work history</li>
                <li>Onboarding and training records</li>
                <li>Signed confidentiality agreements</li>
                <li>Activity and invoicing records</li>
                <li>Audit findings</li>
                <li>Incident, escalation or governance records where relevant</li>
              </ul>
            </div>

            <div className="privacy-subcard" data-testid="privacy-info-patient">
              <h4>Limited patient or clinical information</h4>
              <p>Where pharmacists or prescribers working through Precision Prescribing provide services within a client-controlled platform, they may access patient or clinical information required to complete assigned work.</p>
              <ul>
                <li>Patient personal data</li>
                <li>Health information</li>
                <li>Medication history</li>
                <li>Allergy information</li>
                <li>Clinical questionnaire responses</li>
                <li>Prescribing information</li>
                <li>Treatment suitability information</li>
                <li>Clinical notes</li>
                <li>Task or reference IDs</li>
                <li>Escalation information</li>
                <li>Safeguarding or patient safety information, where relevant</li>
              </ul>
              <p className="privacy-note-inline">Patient records and core clinical data usually remain within the client-controlled platform. Precision Prescribing does not independently host patient records unless expressly agreed in writing.</p>
            </div>
          </AccordionSection>

          {/* 5. How We Use Information (accordion) */}
          <AccordionSection
            id="how-we-use"
            title="5. How We Use Information"
            icon={Users}
            isOpen={!!openAccordions["how-we-use"]}
            onToggle={() => toggleAccordion("how-we-use")}
            testId="privacy-section-how-we-use"
          >
            <p>We may use personal information to:</p>
            <ul className="privacy-list-grid">
              <li>Provide pharmacist, prescriber and clinical support services</li>
              <li>Manage client relationships</li>
              <li>Onboard pharmacists, prescribers and contractors</li>
              <li>Verify professional registration, qualifications and suitability</li>
              <li>Allocate work to appropriate personnel</li>
              <li>Support clinical governance and audit</li>
              <li>Support incident reporting and investigation</li>
              <li>Support safeguarding or patient safety escalation</li>
              <li>Manage service quality and professional standards</li>
              <li>Manage invoicing, payments and activity records</li>
              <li>Comply with legal, regulatory, contractual and professional obligations</li>
              <li>Respond to enquiries, complaints, audits or governance reviews</li>
            </ul>
          </AccordionSection>

          {/* 6. Legal Basis */}
          <section id="legal-basis" className="privacy-block" data-testid="privacy-section-legal-basis">
            <h2><FileText size={22} className="privacy-h2-icon" /> 6. Legal Basis for Processing</h2>
            <p>We process personal data where we have a lawful basis to do so under UK data protection law. Depending on the circumstances, this may include:</p>
            <ul>
              <li>Performance of a contract</li>
              <li>Compliance with legal obligations</li>
              <li>Legitimate interests</li>
              <li>Provision of health or social care</li>
              <li>Reasons of public interest in the area of public health, where applicable</li>
              <li>Establishment, exercise or defence of legal claims</li>
              <li>Professional, regulatory, audit or governance requirements</li>
            </ul>
            <div className="privacy-info-card">
              <p style={{ marginBottom: 0 }}>Where special category health information is processed, this will only be done where necessary and lawful — including where processing is required for healthcare, clinical governance, patient safety, safeguarding, legal or regulatory purposes.</p>
            </div>
          </section>

          {/* 7. Client-Controlled Platforms */}
          <section id="client-platforms" className="privacy-block" data-testid="privacy-section-client-platforms">
            <h2><ShieldCheck size={22} className="privacy-h2-icon" /> 7. Client-Controlled Platforms</h2>
            <p>Where Precision Prescribing personnel work within a client-controlled platform, the client usually controls:</p>
            <ul className="privacy-list-grid">
              <li>The patient-facing service</li>
              <li>Patient records</li>
              <li>System access</li>
              <li>Login credentials</li>
              <li>Role-based permissions</li>
              <li>Clinical pathways</li>
              <li>SOPs</li>
              <li>Escalation routes</li>
              <li>Patient communications</li>
              <li>Complaints handling</li>
              <li>Subject access requests</li>
              <li>Deletion requests</li>
            </ul>
            <p>Precision Prescribing personnel are required to follow the relevant client&rsquo;s approved systems, workflows, SOPs, clinical pathways and escalation routes.</p>
            <div className="privacy-info-card privacy-info-card-warn">
              <p style={{ marginBottom: 0 }}><strong>Personnel must not</strong> download, copy, export, email, print, photograph, screenshot or store patient-identifiable information outside approved systems unless expressly authorised.</p>
            </div>
          </section>

          {/* 8. Data Sharing (accordion) */}
          <AccordionSection
            id="data-sharing"
            title="8. Data Sharing"
            icon={Users}
            isOpen={!!openAccordions["data-sharing"]}
            onToggle={() => toggleAccordion("data-sharing")}
            testId="privacy-section-data-sharing"
          >
            <p>We may share information where necessary with:</p>
            <ul>
              <li>Client organisations</li>
              <li>Pharmacists, prescribers and contractors working on behalf of Precision Prescribing</li>
              <li>Professional advisers</li>
              <li>Insurers or indemnity providers</li>
              <li>Regulators, where legally or professionally required</li>
              <li>NHS or healthcare organisations, where applicable and lawful</li>
              <li>Law enforcement agencies, where legally required</li>
              <li>Service providers who support our business operations</li>
            </ul>
            <div className="privacy-info-card privacy-info-card-highlight">
              <p><strong>We do not sell personal information.</strong></p>
              <p style={{ marginBottom: 0 }}>We do not use customer data for unrelated marketing, resale, AI model training or unrelated secondary purposes.</p>
            </div>
          </AccordionSection>

          {/* 9. Information Security (accordion) */}
          <AccordionSection
            id="security"
            title="9. Information Security"
            icon={Lock}
            isOpen={!!openAccordions.security}
            onToggle={() => toggleAccordion("security")}
            testId="privacy-section-security"
          >
            <p>We take reasonable and proportionate steps to protect personal information. Our controls may include:</p>
            <ul className="privacy-list-grid">
              <li>Confidentiality agreements</li>
              <li>Access on a need-to-know basis</li>
              <li>Role-based access where applicable</li>
              <li>Use of client-approved systems</li>
              <li>MFA where required or available</li>
              <li>No sharing of login credentials</li>
              <li>Device security requirements</li>
              <li>Restrictions on downloading or storing patient data</li>
              <li>Incident reporting procedures</li>
              <li>Audit and governance review</li>
              <li>Secure deletion or return of temporary information</li>
            </ul>
            <p className="privacy-note-inline">Precision Prescribing does not usually host or operate client clinical platforms. Security controls for client platforms are usually managed by the relevant client or their platform provider.</p>
          </AccordionSection>

          {/* 10. External Tools & AI */}
          <section id="external-tools" className="privacy-block" data-testid="privacy-section-external-tools">
            <h2><Bot size={22} className="privacy-h2-icon" /> 10. Use of External Tools and AI</h2>
            <p>Personnel working on behalf of Precision Prescribing must not input patient-identifiable information, client confidential information or clinical case details into unauthorised external tools.</p>
            <p>This includes public AI tools, chatbots, personal cloud storage, personal email accounts, unapproved file-sharing services or any other unauthorised third-party system.</p>
            <div className="privacy-info-card privacy-info-card-highlight">
              <p style={{ marginBottom: 0 }}><strong>Precision Prescribing does not use customer data for AI model training.</strong></p>
            </div>
          </section>

          {/* 11. Data Retention (accordion) */}
          <AccordionSection
            id="retention"
            title="11. Data Retention"
            icon={Clock}
            isOpen={!!openAccordions.retention}
            onToggle={() => toggleAccordion("retention")}
            testId="privacy-section-retention"
          >
            <p>We retain personal information only for as long as reasonably necessary. Retention may be required for:</p>
            <ul className="privacy-list-grid">
              <li>Service delivery</li>
              <li>Contractual purposes</li>
              <li>Legal obligations</li>
              <li>Regulatory obligations</li>
              <li>Clinical governance</li>
              <li>Audit</li>
              <li>Professional indemnity</li>
              <li>Insurance</li>
              <li>Accounting and tax</li>
              <li>Incident investigation</li>
              <li>Complaints handling</li>
              <li>Dispute resolution</li>
            </ul>
            <p>Where information is no longer required, it will be securely deleted, returned or anonymised where appropriate.</p>
            <p className="privacy-note-inline">Where services are delivered through a client-controlled platform, patient records and core clinical data are usually retained by the client in accordance with the client&rsquo;s own retention policies.</p>
          </AccordionSection>

          {/* 12. Individual Rights */}
          <section id="rights" className="privacy-block" data-testid="privacy-section-rights">
            <h2><UserCheck size={22} className="privacy-h2-icon" /> 12. Individual Rights</h2>
            <p>Individuals may have rights under data protection law, including the right to:</p>
            <ul>
              <li>Request access to their personal information</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of information where applicable</li>
              <li>Request restriction of processing</li>
              <li>Object to certain processing</li>
              <li>Request a copy of their information where applicable</li>
              <li>Lodge a complaint with the Information Commissioner&rsquo;s Office</li>
            </ul>
            <p>These rights may be subject to legal, regulatory, professional, contractual, audit or governance requirements.</p>
            <div className="privacy-info-card">
              <p style={{ marginBottom: 0 }}>Where a request relates to patient information held within a client-controlled platform, the request may need to be directed to the relevant client organisation.</p>
            </div>
          </section>

          {/* 13. Patient-Facing Requests */}
          <section id="patient-requests" className="privacy-block" data-testid="privacy-section-patient-requests">
            <h2><MessageSquare size={22} className="privacy-h2-icon" /> 13. Patient-Facing Requests</h2>
            <p>Where Precision Prescribing personnel work within a client-controlled platform, the client is usually responsible for patient-facing requests, including:</p>
            <ul>
              <li>Patient communications</li>
              <li>Complaints</li>
              <li>Subject access requests</li>
              <li>Deletion requests</li>
              <li>Consent processes</li>
              <li>Patient-facing privacy notices</li>
              <li>Patient record management</li>
            </ul>
            <p>If Precision Prescribing receives a patient-facing request relating to a client service, we may direct or escalate the request to the relevant client organisation unless otherwise agreed.</p>
          </section>

          {/* 14. Changes */}
          <section id="changes" className="privacy-block" data-testid="privacy-section-changes">
            <h2><RefreshCw size={22} className="privacy-h2-icon" /> 14. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time.</p>
            <p>Any updated version will be published on this website with a revised &ldquo;Last updated&rdquo; date.</p>
          </section>

          {/* 15. Contact */}
          <section id="contact" className="privacy-block privacy-block-contact" data-testid="privacy-section-contact">
            <h2><Phone size={22} className="privacy-h2-icon" /> 15. Contact Us</h2>
            <p>For privacy, data protection or information security enquiries relating to Precision Prescribing, please contact:</p>

            <div className="privacy-contact-grid">
              <div className="privacy-contact-card" data-testid="privacy-contact-dpo">
                <div className="privacy-contact-badge">Data Protection Lead / SIRO</div>
                <p className="privacy-contact-name">Konstantin Gkioka</p>
                <a href="mailto:kosta@precisionpx.com" className="privacy-contact-email" data-testid="privacy-contact-dpo-email">
                  <Mail size={16} /> kosta@precisionpx.com
                </a>
              </div>

              <div className="privacy-contact-card" data-testid="privacy-contact-guardian">
                <div className="privacy-contact-badge">Caldicott Guardian</div>
                <p className="privacy-contact-name">Neal Sukhadia</p>
                <a href="mailto:neal@precisionpx.com" className="privacy-contact-email" data-testid="privacy-contact-guardian-email">
                  <Mail size={16} /> neal@precisionpx.com
                </a>
              </div>
            </div>
          </section>
        </article>
      </div>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-container">
          <div className="footer-brand" data-testid="footer-brand">
            <span className="logo-text">Precision Prescribing</span>
            <p className="footer-tagline">Pharmacist-led prescribing capacity for regulated healthcare providers.</p>
          </div>
          <div className="footer-info" data-testid="footer-info">
            <p className="footer-legal-line">Precision Prescribing is a trading name of PHARMKG LTD.</p>
            <p>Company number: 14702569</p>
            <p>ICO registration: ZC107443</p>
            <p>Registered office: 261 High Street, Waltham Cross, EN8 7BE</p>
            <p>Email: <a href="mailto:hello@precisionpx.com">hello@precisionpx.com</a></p>
            <nav className="footer-links" data-testid="footer-links" aria-label="Legal">
              <a href="/privacy-policy" className="footer-link" data-testid="footer-privacy-link">Privacy Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/cookie-policy" className="footer-link" data-testid="footer-cookie-link">Cookie Policy</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <a href="/terms-of-use" className="footer-link" data-testid="footer-terms-link">Website Terms of Use</a>
              <span className="footer-sep" aria-hidden="true">|</span>
              <button
                onClick={() => navigate("/", { state: { scrollTo: "contact" } })}
                className="footer-link"
                data-testid="footer-contact-link"
              >
                Contact
              </button>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
